import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { removeUser, selectLoggedInUser } from "store/authSlice";
import { baseApi } from "store/services/baseApi";

export const useAuth = () => {
  const loggedInUser = useSelector(selectLoggedInUser);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const logout = React.useCallback(() => {
    dispatch(removeUser());
    dispatch(baseApi.util.resetApiState());
    navigate("/");
  }, [dispatch, navigate]);

  return React.useMemo(() => {
    if (!loggedInUser) return {};

    const viewEntity = loggedInUser?.viewEntity ?? null;
    const userEntities = loggedInUser?.entities ?? [];

    const checkIfAuthor = (user) => user?._id === loggedInUser?._id;
    const getAuthorName = (user) => {
      if (checkIfAuthor(user)) return "You";
      if (!isAdmin) return user?.name;
      return user?.name;
    };

    const _viewEntityAuthorization = viewEntity?.authorization ?? null;
    const loggedInUserAuthLevel = _viewEntityAuthorization?.level;
    const loggedInUserAuthType = _viewEntityAuthorization?.type;

    // Update isClient to include both "Client" and "Contractor"
    const isClient = loggedInUser?.typeRefSource === "Client";

    const isContractor = loggedInUser?.typeRefSource === "Contractor";
    const isVendor = loggedInUser?.typeRefSource === "VendorProfile";
    const isDriver = loggedInUser?.typeRefSource === "Driver";
    const isVendorStaff = loggedInUser?.typeRefSource === "VendorStaff";
    const isCustomer = loggedInUser?.typeRefSource === "Customer";

    const isReferral = loggedInUser?.typeRefSource === "Referral";
    const isSystem = loggedInUser?.typeRefSource === "System";

    const isAdmin = loggedInUserAuthType === "Admin" && !loggedInUser?.typeRef;
    const isManager = loggedInUserAuthType === "Manager" && !loggedInUser?.typeRef;
    const isSystemAdmin = !!(loggedInUserAuthLevel === "System" && isAdmin);
    const isAccountAdmin = !!(loggedInUserAuthLevel === "Account" && isAdmin);
    const isEntityAdmin = !!(loggedInUserAuthLevel === "Entity" && isAdmin);

    const isEntity = !!(loggedInUserAuthLevel === "Entity");
    const isEntityManager = !!(loggedInUserAuthLevel === "Entity" && isManager);

    return {
      loggedInUser,
      checkIfAuthor,
      getAuthorName,
      viewEntity,
      userEntities,
      isClient,
      isReferral,
      isSystem,
      isAdmin,
      isContractor,
      isVendor,
      isDriver,
      isVendorStaff,
      isCustomer,
      isEntityManager,
      isEntity,
      isEntityAdmin,
      isAccountAdmin,
      isSystemAdmin,
      loggedInUserAuthLevel,
      loggedInUserAuthType,
      logout,
    };
  }, [loggedInUser, logout]);
};
